import React, { useMemo, useState } from "react";
import {
  Box,
  Container,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
  Alert,
  Stack,
} from "@mui/material";

import { ForecastTuningForm } from "./forecast-tuning-form";
import {
  ForecastTuningPolicyEntry,
  useForecastTuningPolicies,
} from "./use-forecast-tuning";

/**
 * Bloque D.8 — UI de afinamiento de `forecast_tuning_policy`. Tabs por
 * `category`. Cada entry edita value_numeric o value_text según corresponda
 * con validación visual de min/max.
 */
export function ConfiguracionForecastPage(): React.ReactElement {
  const { data, isLoading, error } = useForecastTuningPolicies();
  const [activeTab, setActiveTab] = useState<string>("");

  const grouped = useMemo<Record<string, ForecastTuningPolicyEntry[]>>(() => {
    const out: Record<string, ForecastTuningPolicyEntry[]> = {};
    for (const e of data ?? []) {
      if (!out[e.category]) out[e.category] = [];
      out[e.category]!.push(e);
    }
    return out;
  }, [data]);

  const categories = useMemo(() => Object.keys(grouped).sort(), [grouped]);
  const currentTab = activeTab || categories[0] || "";

  if (isLoading) {
    return (
      <Container sx={{ py: 4 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>Cargando políticas...</Typography>
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">
          No se pudo cargar `forecast_tuning_policy`:{" "}
          {error instanceof Error ? error.message : "error desconocido"}
        </Alert>
      </Container>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="info">
          Tabla vacía. Correr `pnpm typeorm migration:run` para seedear.
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }} maxWidth="md">
      <Typography variant="h4" gutterBottom>
        Configuración del forecast
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Afinamiento del sistema sin redeploy. Los cambios se aplican al
        siguiente ciclo del pipeline. Validación min/max obligatoria.
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
        >
          {categories.map((cat) => (
            <Tab key={cat} label={cat} value={cat} />
          ))}
        </Tabs>
      </Box>
      <Stack spacing={1}>
        {(grouped[currentTab] ?? []).map((entry) => (
          <ForecastTuningForm key={entry.key} entry={entry} />
        ))}
      </Stack>
    </Container>
  );
}

export default ConfiguracionForecastPage;
